import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { TopUpPanel } from './TopUpPanel';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatMoney } from '../ui/format';
import { Badge, EmptyState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  cancelCampaign,
  createCampaign,
  estimateCampaign,
  estimateSend,
  getUsage,
  getWallet,
  listCampaigns,
  listLedger,
  listMessages,
  scheduleCampaign,
  sendSms,
  type CampaignView,
  type Estimate,
  type LedgerEntry,
  type SmsMessageView,
} from './api';

/**
 * SMS (context.md sections 72-74).
 *
 * The balance is on every tab, not just the wallet one, because every screen here spends it — and a
 * send that fails for want of credit should be foreseeable from where somebody is standing rather
 * than a surprise after they press the button. The estimate is a first-class step for the same
 * reason: section 74 puts it before the reservation, and so does this screen.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  DELIVERED: 'positive',
  SENT: 'positive',
  RESERVED: 'neutral',
  QUEUED: 'neutral',
  SENDING: 'info',
  UNDELIVERED: 'danger',
  FAILED: 'danger',
  CANCELED: 'warning',
};

const CAMPAIGN_TONE: Record<CampaignView['status'], BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  SENDING: 'info',
  SENT: 'positive',
  CANCELED: 'warning',
  FAILED: 'danger',
};

type Tab = 'send' | 'campaigns' | 'messages' | 'wallet';

export function SmsPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('send');

  const wallet = useQuery({ queryKey: ['sms-wallet'], queryFn: getWallet });

  return (
    <div className="view">
      {/*
       * Said on the screen, not only in the rail (Phase 130).
       *
       * Everything here works — the wallet, the quote, the campaign — and none of it should be
       * used yet: the carrier account is Safe Cortex's to open and the price per message is a
       * commercial decision that is not made. Somebody who reaches this page anyway is told that
       * before they build a campaign on top of it.
       */}
      <p className="alert alert--info">{t('sms.comingSoon')}</p>

      <PageHeader
        title={t('nav.sms')}
        description={t('sms.description')}
        actions={
          wallet.data && (
            <span className="cell-inline">
              {wallet.data.rate_cents !== null && (
                <span className="muted small">
                  {t('sms.rate').replace('{v}', formatMoney(wallet.data.rate_cents))} ·{' '}
                </span>
              )}
              <span className="muted small">{t('sms.balance')}</span>
              <span className="strong">
                {formatMoney(wallet.data.balance_cents, wallet.data.currency)}
              </span>
              {wallet.data.reserved_cents > 0 && (
                <Badge tone="info">
                  {t('sms.heldAmount').replace(
                    '{amount}',
                    formatMoney(wallet.data.reserved_cents, wallet.data.currency),
                  )}
                </Badge>
              )}
              {wallet.data.is_low && <Badge tone="warning">{t('sms.lowBalance')}</Badge>}
            </span>
          )
        }
      />

      <div className="tabs" role="tablist">
        {(['send', 'campaigns', 'messages', 'wallet'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className="tab"
            onClick={() => setTab(key)}
          >
            {t(`sms.tab.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'send' && <SendTab />}
      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'messages' && <MessagesTab />}
      {tab === 'wallet' && <WalletTab />}
    </div>
  );
}

/** Splits what somebody pasted into numbers: commas, semicolons, spaces and newlines all separate. */
function parseRecipients(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function SendTab(): React.JSX.Element {
  const client = useQueryClient();
  const [body, setBody] = useState('');
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof sendSms>> | null>(null);

  const recipients = useMemo(() => parseRecipients(raw), [raw]);
  const ready = body.trim().length > 0 && recipients.length > 0;

  const quote = useMutation({
    mutationFn: () => estimateSend({ body: body.trim(), recipients }),
    onSuccess: (data) => {
      setEstimate(data);
      setError(null);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const send = useMutation({
    mutationFn: () => sendSms({ body: body.trim(), recipients }),
    onSuccess: (data) => {
      setResult(data);
      setEstimate(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['sms-wallet'] });
      void client.invalidateQueries({ queryKey: ['sms-messages'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="home-grid">
      <Panel title={t('sms.compose')}>
        <div className="form-grid">
          <label className="field">
            <span>{t('sms.message')}</span>
            <textarea
              rows={5}
              value={body}
              maxLength={1600}
              placeholder={t('sms.messagePlaceholder')}
              onChange={(event) => {
                setBody(event.target.value);
                setEstimate(null);
              }}
            />
            <span className="muted small">
              {t('sms.characters').replace('{n}', String(body.length))}
            </span>
          </label>

          <label className="field">
            <span>{t('sms.recipients')}</span>
            <textarea
              rows={4}
              value={raw}
              placeholder="+5511999998888, +5521999998888"
              onChange={(event) => {
                setRaw(event.target.value);
                setEstimate(null);
              }}
            />
            <span className="muted small">
              {t('sms.recipientCount').replace('{n}', String(recipients.length))}
            </span>
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="cell-inline">
          <button
            type="button"
            className="button button--quiet"
            disabled={!ready || quote.isPending}
            onClick={() => quote.mutate()}
          >
            {t('sms.estimate')}
          </button>

          <button
            type="button"
            className="button button--primary"
            // Deliberately not enabled until the cost is on screen: a send spends real money, and
            // section 74 puts the estimate before the reservation.
            disabled={!ready || !estimate?.affordable || send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? t('sms.sending') : t('sms.send')}
          </button>
        </div>

        {estimate && <EstimateSummary estimate={estimate} />}

        {result && (
          <div className="stack">
            <p className="alert alert--positive" role="status">
              {t('sms.queuedResult')
                .replace('{n}', String(result.queued.length))
                .replace('{amount}', formatMoney(result.reserved_cents))}
            </p>

            {result.refused.length > 0 && (
              <ul className="home-list">
                {result.refused.map((refusal) => (
                  <li key={refusal.recipient}>
                    <span className="cell-inline">
                      <code>{refusal.recipient}</code>
                      <Badge tone="warning">{t(`sms.refusal.${refusal.reason}`)}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Panel>

      <Panel title={t('sms.usage')}>
        <UsagePanel />
      </Panel>
    </div>
  );
}

function EstimateSummary({ estimate }: { estimate: Estimate }): React.JSX.Element {
  return (
    <div className="stack">
      <dl className="summary">
        <div>
          <dt>{t('sms.priced')}</dt>
          <dd>{estimate.priced}</dd>
        </div>
        <div>
          <dt>{t('sms.segments')}</dt>
          <dd>{estimate.segments}</dd>
        </div>
        <div>
          <dt>{t('sms.cost')}</dt>
          <dd className="strong">{formatMoney(estimate.total_price_cents)}</dd>
        </div>
        <div>
          <dt>{t('sms.balanceAfter')}</dt>
          <dd>{formatMoney(estimate.balance_cents - estimate.total_price_cents)}</dd>
        </div>
      </dl>

      {!estimate.affordable && (
        <p className="alert" role="alert">
          {t('sms.shortfall').replace('{amount}', formatMoney(estimate.shortfall_cents))}
        </p>
      )}

      {estimate.unpriced.length > 0 && (
        <p className="muted small">
          {t('sms.unpriced').replace('{numbers}', estimate.unpriced.join(', '))}
        </p>
      )}
    </div>
  );
}

function UsagePanel(): React.JSX.Element {
  const usage = useQuery({ queryKey: ['sms-usage'], queryFn: getUsage });

  if (usage.isLoading) return <Loading />;
  if (!usage.data || usage.data.messages === 0) {
    return <EmptyState title={t('sms.noUsage')} description={t('sms.noUsageBody')} />;
  }

  return (
    <dl className="summary">
      <div>
        <dt>{t('sms.sentCount')}</dt>
        <dd>{usage.data.messages}</dd>
      </div>
      <div>
        <dt>{t('sms.deliveredCount')}</dt>
        <dd>{usage.data.delivered}</dd>
      </div>
      <div>
        <dt>{t('sms.failedCount')}</dt>
        <dd>{usage.data.failed}</dd>
      </div>
      <div>
        <dt>{t('sms.spent')}</dt>
        <dd className="strong">{formatMoney(usage.data.price_cents)}</dd>
      </div>
    </dl>
  );
}

function CampaignsTab(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [quoted, setQuoted] = useState<Record<string, Estimate>>({});

  const campaigns = useQuery({ queryKey: ['sms-campaigns'], queryFn: listCampaigns });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['sms-campaigns'] });
    void client.invalidateQueries({ queryKey: ['sms-wallet'] });
  };

  const create = useMutation({
    mutationFn: () =>
      createCampaign({
        name: name.trim(),
        body: body.trim(),
        recipients: parseRecipients(raw),
      }),
    onSuccess: () => {
      setName('');
      setBody('');
      setRaw('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const quote = useMutation({
    mutationFn: (id: string) => estimateCampaign(id),
    onSuccess: (data) => setQuoted((current) => ({ ...current, [data.campaign_id]: data })),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const schedule = useMutation({
    mutationFn: (id: string) => scheduleCampaign(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelCampaign(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<CampaignView>[] = [
    {
      key: 'name',
      header: t('sms.campaignName'),
      render: (campaign) => (
        <span className="cell-stack">
          <span className="strong">{campaign.name}</span>
          <span className="muted small">{campaign.body}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (campaign) => (
        <span className="cell-stack">
          <Badge tone={CAMPAIGN_TONE[campaign.status]}>
            {t(`sms.campaign.${campaign.status}`)}
          </Badge>
          {campaign.failure_reason && (
            <span className="muted small">{t(`sms.refusal.${campaign.failure_reason}`)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'result',
      header: t('sms.campaignResult'),
      secondary: true,
      render: (campaign) =>
        campaign.finished_at
          ? t('sms.campaignQueued')
              .replace('{queued}', String(campaign.queued_count))
              .replace('{total}', String(campaign.recipients_total))
              .replace('{amount}', formatMoney(campaign.reserved_cents))
          : quoted[campaign.id]
            ? t('sms.campaignEstimated')
                .replace('{n}', String(quoted[campaign.id]!.recipients))
                .replace('{amount}', formatMoney(quoted[campaign.id]!.total_price_cents))
            : '—',
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (campaign) => (
        <span className="cell-inline">
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => quote.mutate(campaign.id)}
          >
            {t('sms.estimate')}
          </button>

          {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
            <>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={schedule.isPending}
                onClick={() => schedule.mutate(campaign.id)}
              >
                {t('sms.sendNow')}
              </button>

              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(campaign.id)}
              >
                {t('sms.cancel')}
              </button>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Panel title={t('sms.newCampaign')}>
        <div className="form-grid">
          <label className="field">
            <span>{t('sms.campaignName')}</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
          </label>

          <label className="field">
            <span>{t('sms.message')}</span>
            <textarea
              rows={3}
              value={body}
              maxLength={1600}
              onChange={(event) => setBody(event.target.value)}
            />
          </label>

          <label className="field">
            <span>{t('sms.recipients')}</span>
            <textarea rows={3} value={raw} onChange={(event) => setRaw(event.target.value)} />
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={
            name.trim() === '' ||
            body.trim() === '' ||
            parseRecipients(raw).length === 0 ||
            create.isPending
          }
          onClick={() => create.mutate()}
        >
          {create.isPending ? t('work.creating') : t('sms.createCampaign')}
        </button>
      </Panel>

      <Panel>
        <DataTable
          tableKey="sms"
          rows={campaigns.data?.campaigns}
          columns={columns}
          rowKey={(campaign) => campaign.id}
          isLoading={campaigns.isLoading}
          error={campaigns.error}
          onRetry={() => void campaigns.refetch()}
          empty={{ title: t('sms.noCampaigns'), description: t('sms.noCampaignsBody') }}
        />
      </Panel>
    </>
  );
}

function MessagesTab(): React.JSX.Element {
  const [status, setStatus] = useState('');

  const messages = useQuery({
    queryKey: ['sms-messages', status],
    queryFn: () => listMessages(status || undefined),
  });

  const columns: Column<SmsMessageView>[] = [
    {
      key: 'recipient',
      header: t('sms.recipient'),
      render: (message) => (
        <span className="cell-stack">
          <span className="strong">{message.recipient}</span>
          <span className="muted small">{message.body}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (message) => (
        <span className="cell-stack">
          <Badge tone={STATUS_TONE[message.status] ?? 'neutral'}>
            {t(`sms.state.${message.status}`)}
          </Badge>
          {message.failure_reason && (
            <span className="muted small text-down">{message.failure_reason}</span>
          )}
        </span>
      ),
    },
    {
      key: 'segments',
      header: t('sms.segments'),
      secondary: true,
      align: 'end',
      render: (message) => String(message.segments),
    },
    {
      key: 'price',
      header: t('sms.cost'),
      align: 'end',
      render: (message) => formatMoney(message.price_cents),
    },
    {
      key: 'when',
      header: t('sms.queuedAt'),
      align: 'end',
      render: (message) => formatDateTime(message.sent_at ?? message.queued_at),
    },
  ];

  return (
    <>
      <div className="toolbar">
        <select
          className="select"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('email.filterStatus')}
        >
          <option value="">{t('email.allStatuses')}</option>
          {['RESERVED', 'SENDING', 'SENT', 'DELIVERED', 'UNDELIVERED', 'FAILED'].map((entry) => (
            <option key={entry} value={entry}>
              {t(`sms.state.${entry}`)}
            </option>
          ))}
        </select>
      </div>

      <Panel>
        <DataTable
          tableKey="sms-2"
          rows={messages.data?.messages}
          columns={columns}
          rowKey={(message) => message.id}
          isLoading={messages.isLoading}
          error={messages.error}
          onRetry={() => void messages.refetch()}
          empty={{ title: t('sms.noMessages'), description: t('sms.noMessagesBody') }}
        />
      </Panel>
    </>
  );
}

function WalletTab(): React.JSX.Element {
  const ledger = useQuery({ queryKey: ['sms-ledger'], queryFn: () => listLedger(100) });

  const columns: Column<LedgerEntry>[] = [
    {
      key: 'kind',
      header: t('sms.movement'),
      render: (entry) => (
        <span className="cell-stack">
          <Badge tone={entry.amount_cents >= 0 ? 'positive' : 'neutral'}>
            {t(`sms.ledger.${entry.kind}`)}
          </Badge>
          {entry.reason && <span className="muted small">{entry.reason}</span>}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('sms.amount'),
      align: 'end',
      render: (entry) => (
        <span className={entry.amount_cents < 0 ? 'text-down' : 'strong'}>
          {formatMoney(entry.amount_cents)}
        </span>
      ),
    },
    {
      key: 'balance',
      header: t('sms.balanceAfter'),
      align: 'end',
      render: (entry) => formatMoney(entry.balance_after),
    },
    {
      key: 'held',
      header: t('sms.held'),
      secondary: true,
      align: 'end',
      render: (entry) => formatMoney(entry.reserved_after),
    },
    {
      key: 'when',
      header: t('sms.when'),
      align: 'end',
      render: (entry) => formatDateTime(entry.created_at),
    },
  ];

  return (
    <>
      <TopUpPanel />

      <Panel title={t('sms.ledgerTitle')}>
        <p className="muted small">{t('sms.ledgerBody')}</p>

        <DataTable
          tableKey="sms-3"
          rows={ledger.data?.entries}
          columns={columns}
          rowKey={(entry) => entry.id}
          isLoading={ledger.isLoading}
          error={ledger.error}
          onRetry={() => void ledger.refetch()}
          empty={{ title: t('sms.noLedger'), description: t('sms.noLedgerBody') }}
        />
      </Panel>
    </>
  );
}
